import _ from 'lodash';
import { createRouter, createWebHistory } from 'vue-router';

import { useAuthStore } from '@/stores/auth/auth';
import { checkIfIframe } from '@/utils/checkIfIframe';

import helpers from '../helpers/index';

import routes from './routes';
import { IframeStatusMessages } from './utils/iframe-routing/constants';
import {
  processIframeEvents,
  sendIframeResponseEvent,
  validateIframeActions,
  waitForIframeEvent,
} from './utils/iframe-routing/methods';


const flatRoutes = _.flatMap(routes, route => {
  if (_.isEmpty(route.children)) {
    return route;
  }
  return [route].concat(route.children?.map(child => ({
    ...child,
    meta: {
      auth: route.meta?.auth,
    },
  })) || []);
});

const router = createRouter({
  history: createWebHistory(),
  routes,
});

let eventTriggered = false;

router.beforeEach(async (to) => {
  const route = flatRoutes.find(r => r.name === to.name);

  if (!route) {
    return;
  }
  const authStore = useAuthStore();

  // iframe logic
  if (checkIfIframe() && to.meta?.iframeEventRequired) {
    if (!eventTriggered) {
      const eventData = await waitForIframeEvent();
      eventTriggered = true;

      if (validateIframeActions(eventData.action)) {
        return processIframeEvents(eventData.action);
      } else {
        sendIframeResponseEvent(IframeStatusMessages.InvalidAction);
      }
    }
    eventTriggered = false;
  }


  let redirectPathQuery = _.get(to, 'query.redirectPath', '');
  redirectPathQuery = helpers.removeURLParameter(redirectPathQuery as string, 'token');
  if (redirectPathQuery) {
    authStore.setRedirectUrl(redirectPathQuery);
  }
  if (to.meta?.authMult) {
    await authStore.validateMultiUser().catch(() => {});
    sessionStorage.setItem('redirect-mult', to.fullPath);

    // Crutch for single-user tests
    if (to.name !== 'profile-activity') {
      if (!authStore.multiUser) return '/logout';
    }
  }

  if (_.includes(['logout', 'validate-payment'], route.name)
    || /multi-user/.test((typeof route.name == 'string' && route.name)
    || ''
    )) {
    return true;
  }

  if (!to.meta?.allowSuspended && authStore.isSuspended) {
    return '/account/payments';
  }

  if (!to.meta?.allowForceChangePassword && authStore.forceChangePassword) {
    return '/profile/security';
  }

  if (_.isNil(authStore.authorized) && to.name !== 'auth-provider') {
    const initialPath = to.path;
    try {
      await authStore.init({
        initialPath,
        clearCookies: !to.meta?.authMult,
      });

      if (!to.meta?.allowForceChangePassword && authStore.forceChangePassword) {
        return '/profile/security';
      }

      if (initialPath === '/account/payments') {
        try {
          await authStore.getAccountIsPayable();
          return authStore.isPayable ? true : '/account';
        } catch (e) {
          return '/account';
        }
      }

      authStore.getAccountIsPayable();

      if (!to.meta?.allowSuspended && authStore.isSuspended) {
        return '/account/payments';
      }
      return true;
    } catch (e) {
      if (route.path === '*' || route.meta?.auth) { return '/logout'; } else { return true; }
    }
  }

  return true; // eslint-disable-line consistent-return
});

router.afterEach((to) => {
  if (to.meta?.title) {
    document.title = to.meta.title as string;
  }
});

export default router;
